import { GetAllLabWorksQuery } from '@pm/graphql';
import { LabHeroCardProps } from './LabHeroCard';
import {
  buildLabKitHeroCardProps,
  buildLabRequisitionHeroCardProps,
  buildOrphanDocumentBasedResultHeroCardProps,
  LinkedLabResult,
} from './buildLabHeroCardProps';

export type LabWorkNode = NonNullable<
  GetAllLabWorksQuery['labWorks']
>['edges'][number]['node'];

export type ActiveLabWorkNode = Extract<
  LabWorkNode,
  { __typename: 'LabKit' | 'LabRequisition' | 'DocumentBasedLabResult' }
>;

type LabResultNode = Extract<
  LabWorkNode,
  { __typename: 'DetailedLabResult' | 'DocumentBasedLabResult' }
>;

export const isActiveLabWork = (
  node: LabWorkNode,
): node is ActiveLabWorkNode => {
  if (node.__typename === 'LabKit' || node.__typename === 'LabRequisition') {
    return true;
  }
  if (node.__typename === 'DocumentBasedLabResult') {
    return node.labKit == null && node.labRequisition == null;
  }
  return false;
};

export const isLabResultNode = (node: LabWorkNode): node is LabResultNode =>
  node.__typename === 'DetailedLabResult' ||
  node.__typename === 'DocumentBasedLabResult';

// Results carry the authoritative back-reference to their kit/req (populated
// server-side from the MTL order id). Walk the labWorks array once and index by
// the kit/req id so each hero candidate can find its "ready" result in O(1).
export const buildLinkedResultIndex = (
  nodes: LabWorkNode[],
): Map<string, LinkedLabResult> => {
  const index = new Map<string, LinkedLabResult>();
  for (const node of nodes) {
    if (!isLabResultNode(node)) continue;
    const anchor = node.labKit ?? node.labRequisition;
    if (!anchor) continue;
    index.set(anchor.id, node);
  }
  return index;
};

export const heroCardPropsFor = (
  node: ActiveLabWorkNode,
  linkedResult: LinkedLabResult | null,
  supportLink: string,
  isUsaPatient: boolean,
  includeNonActionable = false,
): Exclude<LabHeroCardProps, { kind: 'empty' }> | null => {
  if (node.__typename === 'LabKit') {
    return buildLabKitHeroCardProps(node, linkedResult, {
      supportLink,
      isUsaPatient,
      includeNonActionable,
    });
  }
  if (node.__typename === 'LabRequisition') {
    return buildLabRequisitionHeroCardProps(node, linkedResult, {
      isUsaPatient,
    });
  }
  return buildOrphanDocumentBasedResultHeroCardProps(node);
};
